import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NewObservation, ObservationModel } from '../../../../../../shared/models/observation.model';
import { ObservationTypeDto } from '../../../../../../shared/models/observation-type';
import { BackendLocationModel } from '../../../../../../shared/models/location.model';
import { Subscription } from 'rxjs';
import { ObservationsService } from '../../../../../../data/observations/observations.service';
import { ObservationsManagerService } from '../../../../../../data/observations/observations-manager.service';
import { ToastService } from '../../../../../../shared/services/toast.service';
import { ObservationTypeGroup } from '../../../../../../shared/models/observation-group';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MapEventService } from '../../../../../../shared/components/map-viewer/services/map-event.service';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
  selector: 'app-new-observation',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    MatTooltip,
    MatProgressSpinner,
    MatInput,
    CdkTextareaAutosize,
  ],
  templateUrl: './new-observation.component.html',
  styleUrl: './new-observation.component.scss',
})
export class NewObservationComponent implements OnInit, OnDestroy {
  @Output() observationCreated = new EventEmitter<ObservationModel>();
  @Output() createCanceled = new EventEmitter();

  observationTypeGroupId: number;
  observationTypeId: number;

  observationTypeGroups: ObservationTypeGroup[];
  observationTypes: ObservationTypeDto[];
  selectedType: ObservationTypeDto;
  locationUpdate: BackendLocationModel = null;
  comment: string;

  isSaving = false;

  private locationSubscription: Subscription;

  constructor(
    private readonly observationService: ObservationsService,
    private readonly observationManager: ObservationsManagerService,
    private readonly mapEventService: MapEventService,
    private readonly toast: ToastService
  ) {}

  ngOnInit() {
    this.observationTypeGroups = this.observationManager.getObservationTypeGroups();
    this.locationSubscription = this.observationManager.editedObservationLocation$.asObservable().subscribe(location => {
      this.locationUpdate = location;
    });
  }

  ngOnDestroy() {
    this.locationSubscription?.unsubscribe();
    this.observationManager.stopWaitingForNewObservation();
    this.mapEventService.setObservationPlacementMode(false);
  }

  back() {
    this.createCanceled.emit();
  }

  save() {
    const observation = new NewObservation();
    observation.observationTypeId = this.observationTypeId;
    observation.location = this.locationUpdate;
    observation.comment = this.comment?.substring(0, 256);
    const observationType = this.observationTypes?.find(type => type.id === this.observationTypeId);
    observation.description = observationType?.title ?? '';
    observation.timestamp = new Date();

    this.isSaving = true;
    this.observationService
      .postObservation(observation)
      .then(response => {
        this.toast.long('Observation created.');
        this.locationUpdate = null;
        this.observationCreated.emit(response);
        this.isSaving = false;
      })
      .catch(error => {
        this.isSaving = false;
        this.toast.long('Observation could not be created!');
      });
  }

  canSave(): boolean {
    return this.observationTypeId && !!this.locationUpdate;
  }

  typeGroupChanged(typeGroupId: number) {
    this.observationTypes = this.observationTypeGroups.find(group => group.id === typeGroupId).observationTypes;
    this.observationTypeId = null;
  }

  typeChanged(typeId: number) {
    if (typeId) {
      this.observationManager.waitForNewObservation(this.observationTypeId);
      this.observationManager.updateObservationType(this.observationTypeId);
      this.selectedType = this.observationTypes?.find(type => type.id === typeId);
      this.mapEventService.setObservationPlacementMode(true);
    } else {
      this.observationManager.stopWaitingForNewObservation();
      this.mapEventService.setObservationPlacementMode(false);
    }
  }
}
